import { Input, MenuOption, MenuSection } from "@design";
import { Bot, Search, Sparkle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { AgentSummary } from "@/api";
import { Text } from "@/components/Text";

/** Above this many agents the menu gets a filter field. */
const AGENT_FILTER_THRESHOLD = 7;

/** Assistant picker: plain AnthmAI or one of the caller's agents. */
export function AssistantMenu({
  agents,
  selected,
  onSelect,
  fixedName,
}: Readonly<{
  agents: AgentSummary[];
  selected: string | null;
  onSelect: (agentUuid: string | null) => void;
  /** Once the chat has started its assistant can't change: show it read-only. */
  fixedName?: string;
}>) {
  const { t } = useTranslation("chat", { keyPrefix: "composer" });
  const [filter, setFilter] = useState("");
  if (fixedName !== undefined) {
    return (
      <MenuSection title={t("assistantSection")}>
        <MenuOption icon={selected ? <Bot size={16} /> : <Sparkle size={16} />} description={t("assistantFixed")}>
          {fixedName}
        </MenuOption>
      </MenuSection>
    );
  }
  const query = filter.trim().toLowerCase();
  const shown = query ? agents.filter((a) => `${a.name} ${a.description ?? ""}`.toLowerCase().includes(query)) : agents;
  return (
    <>
      <MenuSection title={t("assistantSection")}>
        <MenuOption
          icon={<Sparkle size={16} />}
          description={t("defaultAssistantDescription")}
          selected={selected === null}
          onSelect={() => onSelect(null)}
        >
          {t("defaultAssistant")}
        </MenuOption>
      </MenuSection>
      {agents.length > 0 && (
        <MenuSection title={t("agentsSection")} divider>
          {agents.length > AGENT_FILTER_THRESHOLD && (
            <div className="px-1 pb-1">
              <Input
                size="sm"
                leadingIcon={<Search size={14} />}
                aria-label={t("filterAgents")}
                placeholder={t("filterAgents")}
                value={filter}
                onChange={(event) => setFilter(event.target.value)}
                // The menu's own keys (Home/End/Space) would otherwise hijack typing; arrows still move into the list.
                onKeyDown={(event) => {
                  if (event.key !== "ArrowDown" && event.key !== "ArrowUp" && event.key !== "Escape") {
                    event.stopPropagation();
                  }
                }}
              />
            </div>
          )}
          {shown.map((agent) => (
            <MenuOption
              key={agent.uuid}
              icon={<Bot size={16} />}
              description={agent.description ?? undefined}
              selected={selected === agent.uuid}
              onSelect={() => onSelect(agent.uuid)}
            >
              {agent.name}
            </MenuOption>
          ))}
          {shown.length === 0 && (
            <Text variant="caption" tone="tertiary" className="px-3 py-2">
              {t("noAgentsMatch")}
            </Text>
          )}
        </MenuSection>
      )}
    </>
  );
}
